import { GENERATED_LANDING_PAGES } from './content.generated';
import { escapeHtml, sectionsHtml, type Section, type StoreLinks } from './blocks';
import { DOC_PAGES, type Crumb } from './docs';
import { LEGAL_CONTENT } from './site';

export type LandingPage = {
  path: string;
  // Short name for the breadcrumb, navigation and related links.
  label: string;
  // <title>, written for the search result.
  title: string;
  // H1, written for the reader.
  heading: string;
  description: string;
  // The direct answer under the H1: the passage search and AI answers quote.
  lead: string;
  // What the header offers: every store, Firefox first, or the paid plans.
  cta: 'stores' | 'firefox' | 'plans';
  updated: string;
  related: string[];
  sections: Section[];
};

// Authored as markdown in content/pages/ and compiled by scripts/build-content.mjs.
export const LANDING_PAGES: Record<string, LandingPage> = Object.fromEntries(
  GENERATED_LANDING_PAGES.map((page) => [page.path, page]),
);

export function landingBreadcrumb(page: LandingPage): Crumb[] {
  return [{ name: 'Home', path: '/' }, { name: page.label, path: page.path }];
}

/** The name a related link shows for a published path. */
export function pageLabel(path: string): string {
  const route = path.split('#')[0];
  return LANDING_PAGES[route]?.label || DOC_PAGES[route]?.label || LEGAL_CONTENT[route.slice(1)]?.title || route;
}

// Everything below the page header. The server snapshot and the React page both
// inject this string, so the two can never disagree about the content.
export function landingArticleHtml(page: LandingPage, links: StoreLinks): string {
  const related = page.related.map((path) => `<a href="${escapeHtml(path)}">${escapeHtml(pageLabel(path))} →</a>`).join('');
  return `${sectionsHtml(page.sections, links)}<nav class="docs-next" aria-label="Related pages">${related}</nav>`;
}

// Shared by the crawlable snapshot and the React footer, so every public page links
// to every landing page from the same place.
export const FOOTER_COLUMNS: Array<[heading: string, links: Array<[label: string, href: string]>]> = [
  ['Product', [
    ['Browser extensions', '/browser-extensions'],
    ['Firefox add-on', '/firefox-extension'],
    ['Pricing', '/pricing'],
    ['Docs', '/guide'],
  ]],
  ['Use cases', [
    ['Email OTP testing', '/email-otp-testing'],
    ['Temporary email for testing', '/temporary-email-for-testing'],
    ['Catch-all email testing', '/catch-all-email-testing'],
    ['Playwright guide', '/docs/playwright-email-testing'],
    ['Cypress guide', '/docs/cypress-email-testing'],
  ]],
  ['Legal', [
    ['Privacy', '/privacy'],
    ['Terms', '/terms'],
    ['Refund Policy', '/refund-policy'],
    ['Acceptable Use', '/acceptable-use'],
  ]],
];

export const NAV_LINKS: Array<[href: string, label: string]> = [
  ['/browser-extensions', 'Extensions'],
  ['/email-otp-testing', 'OTP testing'],
  ['/pricing', 'Pricing'],
  ['/guide', 'Docs'],
];
